// Resource Budget Settings Storage Utilities

import type { ResourceBudgetSettings } from '@/types/resource-budget-settings.types';

const STORAGE_KEY = 'resource-budget-settings';

const DEFAULT_SETTINGS: ResourceBudgetSettings = {
  budgetType: 'simple', // Always enabled with simple budget type
};

export function getResourceBudgetSettings(): ResourceBudgetSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return { ...DEFAULT_SETTINGS, ...parsed };
    }
  } catch (error) {
    console.error('Failed to load resource budget settings:', error);
  }
  return DEFAULT_SETTINGS;
}

export function saveResourceBudgetSettings(settings: ResourceBudgetSettings): void {
  try {
    const settingsWithMetadata = {
      ...settings,
      lastUpdated: new Date().toISOString(),
      updatedBy: 'Current User', // TODO: Replace with actual user
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settingsWithMetadata));
    window.dispatchEvent(new Event('storage'));
  } catch (error) {
    console.error('Failed to save resource budget settings:', error);
    throw error;
  }
}

export function updateResourceBudgetSettings(
  updates: Partial<ResourceBudgetSettings>
): void {
  const current = getResourceBudgetSettings();
  saveResourceBudgetSettings({ ...current, ...updates });
}

export function resetResourceBudgetSettings(): void {
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new Event('storage'));
}

// Helper to calculate summary
export function calculateBudgetSummary(settings: ResourceBudgetSettings) {
  if (settings.budgetType === 'simple' && settings.roleBudgets) {
    const totalHours = settings.roleBudgets.reduce(
      (sum, role) => sum + role.budgetHours,
      0
    );
    const totalEstimatedCost = settings.roleBudgets.reduce(
      (sum, role) => sum + role.estimatedCost,
      0
    );
    return {
      totalHours,
      totalEstimatedCost,
      roleCount: settings.roleBudgets.length,
    };
  }
  
  if (settings.budgetType === 'monthly' && settings.monthlyRoleBudgets) {
    const totalHours = settings.monthlyRoleBudgets.reduce(
      (sum, role) => sum + role.budgetHours,
      0
    );
    const totalEstimatedCost = settings.monthlyRoleBudgets.reduce(
      (sum, role) => sum + role.estimatedCost,
      0
    );
    return {
      totalHours,
      totalEstimatedCost,
      roleCount: settings.monthlyRoleBudgets.length,
    };
  }
  
  return {
    totalHours: 0,
    totalEstimatedCost: 0,
    roleCount: 0,
  };
}
